import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { Collapse, Input, Tag } from 'antd';

import styled from 'styled-components';
import fontService, { FontWeight, TextType } from 'services/FontService';
import { Colors } from 'context';
import { Button, ButtonSizes } from 'atoms';

export interface ITodayLinkPanel {
  linkOverrideActive: boolean;
  setLoading: boolean;
  clearLoading: boolean;
  /** Returns true on success — the form is reset then. */
  onSetLink: (joinUrl: string) => Promise<boolean>;
  onClearLink: () => void;
}

interface IFormValues {
  joinUrl: string;
}

const URL_PATTERN = /^https?:\/\/\S+$/;

/**
 * Spec 0013: teacher-only collapsed panel — temporary "today only" join link
 * for the group (e.g. a substitute teacher's personal Zoom room).
 */
export const TodayLinkPanel = memo(
  ({ linkOverrideActive, setLoading, clearLoading, onSetLink, onClearLink }: ITodayLinkPanel) => {
    const { t } = useTranslation();

    const validationSchema = useMemo(
      () =>
        Yup.object().shape({
          joinUrl: Yup.string()
            .trim()
            .required(`${t('lesson.join.urlRequired')}`)
            .matches(URL_PATTERN, `${t('lesson.join.urlInvalid')}`),
        }),
      [t],
    );

    return (
      <PanelWrapper>
        <Collapse ghost>
          <Collapse.Panel header={t('lesson.join.overrideTitle')} key="today-link">
            {linkOverrideActive && (
              <OverrideRow>
                <Tag color="gold">{t('lesson.join.overrideActive')}</Tag>
                <Button
                  variant="text"
                  size={ButtonSizes.sm}
                  sidePaddings={8}
                  onClick={onClearLink}
                  isLoading={clearLoading}
                  disabled={clearLoading}>
                  {t('lesson.join.clearOverride')}
                </Button>
              </OverrideRow>
            )}

            <Formik<IFormValues>
              initialValues={{ joinUrl: '' }}
              validationSchema={validationSchema}
              onSubmit={async (values, { resetForm }) => {
                const success = await onSetLink(values.joinUrl.trim());
                if (success) {
                  resetForm();
                }
              }}>
              {({ values, errors, touched, setFieldValue, setFieldTouched, handleSubmit }) => (
                <Form onSubmit={handleSubmit}>
                  <InputRow>
                    <Input
                      value={values.joinUrl}
                      placeholder={`${t('lesson.join.overridePlaceholder')}`}
                      onChange={(event) => setFieldValue('joinUrl', event.target.value)}
                      onBlur={() => setFieldTouched('joinUrl')}
                    />
                    <Button
                      type="submit"
                      size={ButtonSizes.sm}
                      sidePaddings={16}
                      isLoading={setLoading}
                      disabled={setLoading}>
                      {t('lesson.join.setOverride')}
                    </Button>
                  </InputRow>
                  {touched.joinUrl && errors.joinUrl && <ErrorText>{errors.joinUrl}</ErrorText>}
                </Form>
              )}
            </Formik>
          </Collapse.Panel>
        </Collapse>
      </PanelWrapper>
    );
  },
);

const PanelWrapper = styled.div`
  margin-top: 8px;

  .ant-collapse-header {
    padding-left: 0 !important;
    ${fontService.text({ type: TextType.normal, weight: FontWeight.medium })}
  }

  .ant-collapse-content-box {
    padding-left: 0 !important;
  }
`;

const OverrideRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
`;

const Form = styled.form`
  width: 100%;
`;

const InputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 480px;
`;

const ErrorText = styled.p`
  margin: 4px 0 0;
  color: ${Colors.signal_red};
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}
`;
